import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ButtonBase } from '@mui/material';
import { IconEdit, IconTrash, IconPlus } from '@tabler/icons-react';
import MainCard from "ui-component/cards/MainCard";
import SearchSection from "layout/MainLayout/Header/SearchSection";

const EditButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  '&:hover': {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: 'pointer'
}));

const DeleteButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.error.dark,
  '&:hover': {
    background: theme.palette.error.dark,
    color: theme.palette.error.light
  },
  width: 40,
  height: 40,
  borderRadius: 12,
  cursor: 'pointer'
}));

const AddButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.info.dark,
  '&:hover': {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light
  },
  align: 'center',
  gap: 8,
  width: 150,
  height: 40,
  borderRadius: 10,
  display: 'flex',
  cursor: 'pointer'
}));

const StreetsList = () => {
  const [streets, setStreets] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentStreet, setCurrentStreet] = useState({ name: "" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const fetchStreets = useCallback(() => {
    const searchText = searchTerm !== '' ? `&contains=${searchTerm}` : '';
    fetch(`/backend/streets/?page=${page + 1}&page_size=${rowsPerPage}${searchText}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Falha ao carregar as ruas");
        }
        return response.json();
      })
      .then((data) => {
        setStreets(data.results);
        setTotalRecords(data.count);
      })
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchStreets();
  }, [fetchStreets, page, rowsPerPage, searchTerm]);


  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = (street) => {
    setCurrentStreet(street);
    setIsEditing(true);
    setOpenModal(true);
  };

  const handleAdd = () => {
    setCurrentStreet({ name: "", state: "" , city: null});
    setIsEditing(false);
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleSave = () => {
    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `/backend/streets/${currentStreet.id}/`
      : "/backend/streets/";

    fetch(url, {
      method: method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(currentStreet),
    })
      .then((response) => {
        if (response.ok) return response.json();
        throw new Error("Erro ao salvar rua");
      })
      .then((data) => {
        if (isEditing) {
          setStreets((prevStreets) =>
            prevStreets.map((street) => (street.id === data.id ? data : street))
          );
        } else {
          setStreets((prevStreets) => [...prevStreets, data]);
        }
        alert("Rua salva com sucesso!");
        setOpenModal(false);
        fetchStreets();
      })
      .catch((error) => alert("Erro: " + error.message));
  };

  const handleDelete = (streetId) => {
    if (window.confirm("Você tem certeza que deseja excluir esta rua?")) {
      fetch(`/backend/streets/${streetId}/`, {
        method: "DELETE",
      })
        .then((response) => {
          if (response.ok) {
            setStreets((prevStreets) => prevStreets.filter((street) => street.id !== streetId));
            alert("Rua excluída com sucesso!");
            fetchStreets();
          } else {
            throw new Error("Erro ao excluir rua");
          }
        })
        .catch((error) => alert("Erro: " + error.message));
    }
  };

  return (
    <div className="w-full overflow-auto">
      <MainCard title="Cadastro de ruas"
        actions={[
          <AddButton onClick={handleAdd}><IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />Adicionar rua</AddButton>
        ]}>
        <SearchSection onSearchChange={setSearchTerm} />
        <Divider sx={{ my: 2 }} />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {streets.map((street) => (
              <TableRow key={street.id}>
                <TableCell>{street.name}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <EditButton onClick={() => handleEdit(street)}>
                      <IconEdit stroke={1.5} size="1.3rem" />
                    </EditButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <DeleteButton onClick={() => handleDelete(street.id)}>
                      <IconTrash stroke={1.5} size="1.3rem" />
                    </DeleteButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MainCard>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>{isEditing ? "Editar Rua" : "Adicionar Rua"}</DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          <TextField label="Nome" value={currentStreet.name} onChange={(e) => setCurrentStreet({ ...currentStreet, name: e.target.value })} fullWidth sx={{ mt: 2,  mb: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">Cancelar</Button>
          <Button onClick={handleSave} color="primary">Salvar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StreetsList;
